import React, {
  useDeferredValue,
  useMemo,
  useState,
} from "react";
import { FlatList, View } from "react-native";
import {
  AHEmptyScreen,
  AHIcon,
  AHSeparator,
  AHText,
  Box,
  ColorType,
  ListItem,
} from "arkh";
import { graphql, useFragment, useLazyLoadQuery } from "react-relay";
import { SegmentedControlNew } from "@/src/components/SegmentedControlNew/SegmentedControlNew";
import { AnalyticsTimeResolution } from "../../WorkspaceAnalytics/__generated__/WorkspaceAnalyticsDetailQuery.graphql";

import { LazyQueryNetworkBoundrary } from "@/src/components/mobile/QueryNetworkBoundrary";
import { WorkspaceRoutineStatusOverviewQuery } from "./__generated__/WorkspaceRoutineStatusOverviewQuery.graphql";
import { WorkspaceRoutineStatusOverviewListItem_dimension$key } from "./__generated__/WorkspaceRoutineStatusOverviewListItem_dimension.graphql";
import { Image } from "expo-image";
import { FormattedMessage } from "react-intl";

import { useOrgListStyleProps } from "../../useOrgHomeDetailContainerStyle";
import { OrgSearchInput } from "../../OrgSearchInput";
import { SortableColumnTitle } from "../../SortableColumnTitle";
import { OrgTableTitle } from "../../OrgTable/OrgTable";
import { DateTime } from "luxon";
import { useIntlTokens } from "@/src/components/mobile/useIntlTokens";
import { useWorkspaceId } from "../../workspaceId";

type RoutineAnalyticsProps = {
  routineWorkspaceId: string;
  fetchKey: string;
};
export function WorkspaceRoutineStatusOverviewTabScreen(props: {
  routineWorkspaceId: string;
}) {
  return (
    <LazyQueryNetworkBoundrary newFetchKeyOnNavigation>
      {(analyticsFetchKey) => (
        <WorkspaceRoutineStatusOverview
          routineWorkspaceId={props.routineWorkspaceId}
          fetchKey={analyticsFetchKey}
        />
      )}
    </LazyQueryNetworkBoundrary>
  );
}

function WorkspaceRoutineStatusOverview(props: RoutineAnalyticsProps) {
  const [timeResolution, setTimeResolution] =
    useState<AnalyticsTimeResolution>("WEEK");

  const deferredTimeResolution = useDeferredValue(timeResolution);

  const workspaceId = useWorkspaceId();
  const data = useLazyLoadQuery<WorkspaceRoutineStatusOverviewQuery>(
    graphql`
      query WorkspaceRoutineStatusOverviewQuery(
        $routineWorkspaceId: ID!
        $timeResolution: AnalyticsTimeResolution!
        $workspaceId: ID!
      ) {
        routineWorkspace(id: $routineWorkspaceId) {
          completionDimensions(
            timeResolution: $timeResolution
            workspaceId: $workspaceId
          ) {
            label

            completedCount
            totalCount
            startedCount
            ...WorkspaceRoutineStatusOverviewListItem_dimension
          }
        }
      }
    `,
    {
      routineWorkspaceId: props.routineWorkspaceId,
      timeResolution: deferredTimeResolution,
      workspaceId,
    },
    {
      fetchKey: props.fetchKey,
      fetchPolicy: "store-and-network",
    }
  );
  const [search, setSearch] = useState("");
  const [order, setOrder] = useState<"ASC" | "DESC">("DESC");

  const listStyle = useOrgListStyleProps();
  const remoteData = useMemo(
    () => data.routineWorkspace?.completionDimensions ?? [],
    [data.routineWorkspace?.completionDimensions]
  );
  const dimensions = useMemo(() => {
    const d = remoteData.filter((dimension) =>
      dimension.label.toLowerCase().includes(search.toLowerCase())
    );
    if (order === "ASC") {
      return d.reverse();
    } else {
      return d;
    }
  }, [remoteData, order, search]);

  const weekLabel = useMemo(() => {
    return DateTime.now().minus({ weeks: 1 }).toFormat("vW");
  }, []);

  const monthLabel = useMemo(() => {
    return DateTime.now().minus({ months: 1 }).toLocaleString({
      month: "long",
    });
  }, []);
  const intl_tokens = useIntlTokens();

  return (
    <Box spaceTop="medium" style={{ flex: 1 }} color="surface-background-light">
      {remoteData.length === 0 ? (
        <Box style={{ flex: 1 }}>
          <AHEmptyScreen
            title="No data"
            subtitle={
              <FormattedMessage
                defaultMessage="No data"
                description="WorkspaceRoutineStatusOverview empty subtitle"
              />
            }
            illustration={
              <Image
                contentFit="contain"
                style={{ flex: 1 }}
                source={require("../../WorkspaceRoutines/WorkspaceComplianceRoutines/Checklist.png")}
              />
            }
          />
        </Box>
      ) : (
        <FlatList
          {...listStyle}
          stickyHeaderIndices={[0]}
          data={dimensions}
          ListHeaderComponent={
            <Box color="surface-background-light">
              <Box spaceBottom="medium" style={{ flexDirection: "row" }}>
                <SegmentedControlNew
                  items={[
                    {
                      children: <AHText type="subtitle">{weekLabel}</AHText>,
                      value: "WEEK" satisfies AnalyticsTimeResolution,
                    },
                    {
                      children: <AHText type="subtitle">{monthLabel}</AHText>,
                      value: "MONTH" satisfies AnalyticsTimeResolution,
                    },
                  ]}
                  selected={timeResolution}
                  setSelected={(r) => setTimeResolution(r)}
                />
              </Box>

              <OrgSearchInput value={search} onChangeText={setSearch} />
              <ListItem disabled border="border-primary" displayMode="TABLE">
                <WorkspaceRoutineStatusOverviewWrapper
                  title={
                    <SortableColumnTitle
                      title={intl_tokens.Name}
                      onOrderBySelect={() => {}}
                      order={null}
                      onOrderChange={setOrder}
                    />
                  }
                  status={
                    <SortableColumnTitle
                      title={intl_tokens.Status}
                      onOrderBySelect={() => {
                        setOrder(order === "ASC" ? "DESC" : "ASC");
                      }}
                      order={order}
                      onOrderChange={setOrder}
                    />
                  }
                />
              </ListItem>
              <AHSeparator />
            </Box>
          }
          ItemSeparatorComponent={AHSeparator}
          renderItem={({ item, index }) => (
            <WorkspaceRoutineStatusOverviewListItem
              dimension={item}
              isLast={index === dimensions.length - 1}
            />
          )}
          ListEmptyComponent={null}
        />
      )}
    </Box>
  );
}

function WorkspaceRoutineStatusOverviewListItem(props: {
  dimension: WorkspaceRoutineStatusOverviewListItem_dimension$key;
  isLast: boolean;
}) {
  const data = useFragment(
    graphql`
      fragment WorkspaceRoutineStatusOverviewListItem_dimension on RoutineWorkspaceCompletionDimension {
        id
        label
        completedCount
        totalCount
        startedCount
      }
    `,
    props.dimension
  );

  if (data.totalCount === 0) return null;

  const notCompletedCount =
    data.totalCount - data.startedCount - data.completedCount;

  const height = 25;

  return (
    <ListItem
      disabled
      border="border-primary"
      displayMode="TABLE"
      isLast={props.isLast}
    >
      <WorkspaceRoutineStatusOverviewWrapper
        title={
          <Box style={{ flexDirection: "row", alignItems: "center" }}>
            <AHIcon type="store" spaceRight="small" color="text-washed" />
            <OrgTableTitle>{data.label}</OrgTableTitle>
          </Box>
        }
        completed={<StatText color="positive" value={data.completedCount} />}
        started={<StatText color="semiNegative" value={data.startedCount} />}
        notCompleted={<StatText color="negative" value={notCompletedCount} />}
        status={
          <View style={{ flexDirection: "row", alignItems: "center", flex: 1 }}>
            <Box
              style={{
                flex: 1,
                flexDirection: "row",
                overflow: "hidden",
                height: height,
              }}
              cornerRadius="medium"
            >
              <Box
                color="negative"
                style={{ flex: notCompletedCount, height }}
              />
              <Box
                color="semiNegative"
                style={{ flex: data.startedCount, height }}
              />
              <Box
                color="positive"
                style={{ flex: data.completedCount, height }}
              />
            </Box>
          </View>
        }
      ></WorkspaceRoutineStatusOverviewWrapper>
    </ListItem>
  );
}
function StatText(props: { value: number; color: ColorType }) {
  return (
    <Box
      borderColor={props.color}
      style={{ borderWidth: 2 }}
      space="xxsmall"
      cornerRadius="medium"
    >
      <AHText type="subtitle" color="text-primary" textAlign="center">
        {props.value}
      </AHText>
    </Box>
  );
}

function WorkspaceRoutineStatusOverviewWrapper(props: {
  title: React.ReactNode;
  started?: React.ReactNode;
  completed?: React.ReactNode;
  notCompleted?: React.ReactNode;
  status: React.ReactNode;
}) {
  return (
    <Box style={{ flexDirection: "row", alignItems: "center" }}>
      <Box style={{ width: 200 }} spaceRight="medium">
        {props.title}
      </Box>
      <Box
        style={{
          flex: 1,

          flexDirection: "row",
          alignItems: "flex-end",
          justifyContent: "flex-end",
        }}
        spaceRight="small"
      >
        <Box style={{ width: 40 }} spaceRight="medium">
          {props.started}
        </Box>
        <Box style={{ width: 40 }} spaceRight="medium">
          {props.completed}
        </Box>
        <Box style={{ width: 40 }} spaceRight="medium">
          {props.notCompleted}
        </Box>
      </Box>

      <Box
        style={{
          flex: 1,

          flexDirection: "row",
          alignItems: "flex-end",
          justifyContent: "flex-end",
        }}
      >
        <Box style={{ flexDirection: "row", flex: 1, maxWidth: 400 }}>
          {props.status}
        </Box>
      </Box>
    </Box>
  );
}
